import { useEffect, useRef } from 'react'

export const uploadCompleteKey = bpId => `upload-complete-${bpId}`
export const FILES_REFRESH_EVENT = 'bp:files-refresh'
export const FOLLOW_UP_INTERVAL_MS = 3000
export const FOLLOW_UP_MAX_MS = 120000

export const notifyUploadComplete = (bpId, { count = 0, names = [] } = {}) => {
  if (!bpId) return
  const key = uploadCompleteKey(bpId)
  const value = JSON.stringify({ at: Date.now(), count: Number(count) || 0, names: Array.isArray(names) ? names.slice(0, 50) : [] })
  try { localStorage.setItem(key, value) } catch { }
  try { window.dispatchEvent(new StorageEvent('storage', { key, newValue: value })) } catch { }
}

export const parseUploadComplete = value => {
  try {
    const parsed = JSON.parse(value)
    if (parsed && typeof parsed === 'object') {
      return { count: Number(parsed.count) || 0, names: Array.isArray(parsed.names) ? parsed.names : [] }
    }
  } catch { }
  return { count: 0, names: [] }
}

export const notifyFilesRefresh = bpId => {
  if (!bpId) return
  try { window.dispatchEvent(new CustomEvent(FILES_REFRESH_EVENT, { detail: { bpId: String(bpId) } })) } catch { }
}

export const useFilesRefresh = (bpId, handler) => {
  const ref = useRef(handler)
  useEffect(() => { ref.current = handler })
  useEffect(() => {
    if (!bpId) return undefined
    const onRefresh = event => {
      if (String(event.detail?.bpId) === String(bpId)) ref.current?.()
    }
    window.addEventListener(FILES_REFRESH_EVENT, onRefresh)
    return () => window.removeEventListener(FILES_REFRESH_EVENT, onRefresh)
  }, [bpId])
}

export const useUploadComplete = (bpId, handler) => {
  const ref = useRef(handler)
  useEffect(() => { ref.current = handler })
  useEffect(() => {
    if (!bpId) return undefined
    const key = uploadCompleteKey(bpId)
    const onStorage = event => {
      if (event.key === key && event.newValue) ref.current?.(parseUploadComplete(event.newValue))
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [bpId])
}

export const useUploadFollowUp = (bpId, { onTick, readTotal, intervalMs = FOLLOW_UP_INTERVAL_MS, maxMs = FOLLOW_UP_MAX_MS }) => {
  const tickRef = useRef(onTick)
  const totalRef = useRef(readTotal)
  const pollRef = useRef(null)
  useEffect(() => { tickRef.current = onTick; totalRef.current = readTotal })

  const stop = () => {
    if (pollRef.current?.timer) clearInterval(pollRef.current.timer)
    pollRef.current = null
  }

  useEffect(() => stop, [bpId])

  useUploadComplete(bpId, ({ count }) => {
    const current = Number(totalRef.current?.()) || 0
    const expectedNew = Math.max(1, count)
    const previous = pollRef.current
    const expected = previous ? previous.expected + expectedNew : current + expectedNew
    if (previous?.timer) clearInterval(previous.timer)
    const deadline = Date.now() + maxMs
    const poll = { expected, deadline, timer: null }
    pollRef.current = poll
    tickRef.current?.()
    poll.timer = setInterval(() => {
      if (pollRef.current !== poll) return
      const total = Number(totalRef.current?.()) || 0
      if (total >= poll.expected || Date.now() >= poll.deadline) {
        stop()
        return
      }
      tickRef.current?.()
    }, intervalMs)
  })
}
